import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslation } from '@/hooks/useTranslation';

interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog should close */
  onClose: () => void;
  /** Callback when user confirms the action */
  onConfirm: () => void;
  /** Dialog title. Defaults to translated confirmation title */
  title?: string;
  /** Dialog description. Defaults to translated confirmation message */
  description?: string;
  /** Confirm button label. Defaults to translated confirm label */
  confirmLabel?: string;
  /** Cancel button label. Defaults to translated cancel label */
  cancelLabel?: string;
  /** Variant for the confirm button. Defaults to 'default' */
  confirmVariant?:
    'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

/**
 * Reusable confirmation dialog component using Shadcn AlertDialog.
 *
 * Features:
 * - Fully accessible with proper ARIA labels
 * - Internationalization support with default translations
 * - Customizable title, description, and button labels
 * - Configurable confirm button variant
 * - Follows project's design patterns and best practices
 *
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   isOpen={showConfirmation}
 *   onClose={() => setShowConfirmation(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item? This action cannot be undone."
 *   confirmLabel="Delete"
 *   confirmVariant="destructive"
 * />
 * ```
 */
export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'default',
}: ConfirmationDialogProps) {
  const { t } = useTranslation();

  // Default values from translations
  const defaultTitle = title ?? t('common.confirmation.title');
  const defaultDescription =
    description ?? t('common.confirmation.description');
  const defaultConfirmLabel = confirmLabel ?? t('common.actions.confirm');
  const defaultCancelLabel = cancelLabel ?? t('common.actions.cancel');

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{defaultTitle}</AlertDialogTitle>
          <AlertDialogDescription>{defaultDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {defaultCancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={
              confirmVariant === 'destructive'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : undefined
            }
          >
            {defaultConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
